import {
  CompRef,
  PresetsValue,
  WidgetDescriptor,
} from '@wix/document-services-types';
import { ComponentDefinition } from '@wix/editor-platform-sdk-types';
import { PLATFORM_BREAKPOINT_RANGES } from '@wix/editor-platform-host-integration-apis';
import {
  ExtendedPlatformContext,
  PlatformContext,
} from '../../../../../types/platformApi';
import {
  AddToStageData,
  AppData,
  UnifiedWidget,
  BuilderComponent,
  StaticContainer,
} from '../../../../../types/unifiedComponents';
import {
  AddUnifiedComponentsErrorCode as ErrorCode,
  createAddUnifiedComponentsError,
} from '../errors';
import { getWidgetAppearanceData } from './addBlocksWidget';
import { getLayouts } from '../utils';

import { EditorPointer } from '@wix/editor-document-logic-api';

const TOKEN = 'unified-components-installation';

export async function addBuilderWidgetComponent(
  context: ExtendedPlatformContext,
  appData: AppData,
  widgetDescriptor: WidgetDescriptor,
  widgetDefinition: UnifiedWidget,
  containerRef: CompRef,
  addToStageData: AddToStageData,
): Promise<CompRef> {
  const { widgetId, componentModel } = widgetDefinition;
  const isStretched = !!widgetDefinition.isStretched?.desktop;
  const containerLayout =
    context.documentServices.components.layout.get(containerRef);

  const { presetId, layout, layouts } = getWidgetAppearanceData({
    widgetDefinition,
    widgetDescriptor,
    presetId: addToStageData.presetId,
    mobilePresetId: addToStageData.mobilePresetId,
    position: addToStageData.position,
    containerLayout,
    isStretched,
  });

  const componentType = componentModel?.componentType;

  const componentDefinition: ComponentDefinition = {
    componentType,
    type: 'Component',
    layout,
    layouts,
  };

  if (presetId) {
    componentDefinition.presets = {
      type: 'PresetData',
      layout: presetId,
      style: presetId,
    } as PresetsValue;
  }

  try {
    const compRef = await context.platformApiMethods.document.components.add(
      appData,
      TOKEN,
      {
        componentDefinition,
        pageRef: containerRef,
      },
    );

    return compRef;
  } catch (error) {
    throw createAddUnifiedComponentsError(
      ErrorCode.addingBuilderComponentFailed,
      'Could not add builder component',
    )
      .withBuilderComponentType(componentType)
      .withUserComponentId(widgetId, 'builder-component')
      .withParentError(error as Error)
      .withAppDefIds(appData.appDefinitionId);
  }
}

export const applyPresets = (context: PlatformContext, compRef: CompRef) => {
  const buildEditorPointer =
    context.editorPlatformPackagesBridge.EditorPointerAPI?.buildEditorPointer;

  if (!buildEditorPointer) {
    return;
  }

  const editorPointer = buildEditorPointer(compRef);
  if (!editorPointer) {
    console.error(
      'editorPlatformPackagesBridge or EditorPointerAPI is not initialized, skipping preset application',
    );
    return;
  }

  const installation =
    context.componentsPlatformService.components.getInstallation(editorPointer);

  // TODO: [EP-7054] replace large with default
  const {
    default: defaultPreset,
    large,
    ...restOfBreakpointsPresets
  } = installation?.presets ?? {};

  const installationPreset = defaultPreset ?? large;
  if (installationPreset) {
    context.componentsPlatformService.presets.applyPreset(
      editorPointer,
      installationPreset,
    );
  }

  if (restOfBreakpointsPresets) {
    Object.entries(restOfBreakpointsPresets).forEach(([breakpoint, preset]) => {
      if (!preset) {
        return;
      }

      const breakpointRange = PLATFORM_BREAKPOINT_RANGES.find(
        (range) => range.size === breakpoint,
      );

      if (!breakpointRange) {
        return;
      }

      const breakpointPointer =
        context.platformApiMethods.editor.stage?.getPointerWithBreakpointVariant(
          editorPointer,
          breakpointRange,
        ) as EditorPointer<CompRef>;

      if (!breakpointPointer) {
        return;
      }

      context.componentsPlatformService.presets.applyPreset(
        breakpointPointer,
        preset,
      );
    });
  }
};

export async function addBuilderComponent(
  context: ExtendedPlatformContext,
  appData: AppData,
  builderComponentDefinition: BuilderComponent,
  containerRef: CompRef,
  installationOptions?: {
    staticContainer?: StaticContainer;
  },
): Promise<CompRef> {
  const { staticContainer } = installationOptions ?? {};
  const { data, componentId } = builderComponentDefinition;

  const initialSize =
    builderComponentDefinition?.data?.installation?.initialSize;

  const layouts = getLayouts(context, {
    initialSize,
    containerRef,
    staticContainer,
  });

  const componentType = data.type!;

  const componentDefinition: ComponentDefinition = {
    componentType,
    type: 'Component',
    layouts,
  };

  try {
    let compRef: CompRef;

    if (staticContainer === StaticContainer.GLOBAL_PAGE) {
      const editorPointer =
        await context.platformApiMethods.document.components.addAsGlobal(
          appData,
          TOKEN,
          componentDefinition,
        );
      compRef = editorPointer.documentPointer as CompRef;
    } else {
      compRef = await context.platformApiMethods.document.components.add(
        appData,
        TOKEN,
        {
          componentDefinition,
          pageRef: containerRef,
        },
      );
    }

    try {
      applyPresets(context, compRef);
    } catch (e) {
      const error = createAddUnifiedComponentsError(
        ErrorCode.applyingPresetsFailed,
        'Could not apply presets',
      )
        .withBuilderComponentType(componentType)
        .withUserComponentId(componentId, 'builder-component')
        .withParentError(e as Error)
        .withAppDefIds(appData.appDefinitionId);
      // TODO: [EP-7982] report BI
      console.error(error);
    }

    return compRef;
  } catch (error) {
    throw createAddUnifiedComponentsError(
      ErrorCode.addingBuilderComponentFailed,
      'Could not add builder component',
    )
      .withBuilderComponentType(componentType)
      .withUserComponentId(componentId, 'builder-component')
      .withParentError(error as Error)
      .withAppDefIds(appData.appDefinitionId);
  }
}
